/**
 * 局势查询：把「现在能做什么、为什么不能做」收敛成一份纯函数输出
 *
 * 为什么放在 shared 而不是客户端：这些判定同时是**模拟核心的规则**和
 * **UI 的可用性状态**。如果客户端自己再写一套「这条路能不能走」，
 * 迟早会和 simulate() 的行为对不上（点了没反应的按钮是最典型的 bug，
 * 也是最容易被孩子误解为"游戏坏了"的那种）。
 *
 * 纪律：纯函数，禁止 Date.now / Math.random。
 */

import { IGNITE_COST, resolveConstants } from "./constants.js";
import { collectEffects, spreadCost, type SkillEffects } from "./effects.js";
import type { MatchSetup, Region, Route, SimState, SkillNode } from "./types.js";

/** 不能执行某个操作的原因（UI 直接把它翻成中文提示） */
export type BlockReason =
  // 路线相关
  | "route_unknown"
  | "route_not_revealed"
  | "route_closed"
  | "route_need_card"
  | "source_not_ignited"
  | "region_full"
  | "insufficient_energy"
  // 点燃相关
  | "region_unknown"
  | "already_ignited"
  | "not_adjacent"
  // 技能树相关
  | "node_unknown"
  | "node_already_unlocked"
  | "node_need_parent"
  | "insufficient_wisdom";

export interface RouteOption {
  route: Route;
  /** 是否可以立刻传播 */
  usable: boolean;
  /** 本次传播的能量花费 */
  cost: number;
  /** 目标区域当前启蒙度 */
  targetEnlightenment: number;
  /** usable 为 false 时的原因 */
  reason?: BlockReason;
}

export interface RegionOption {
  region: Region;
  ignitable: boolean;
  reason?: BlockReason;
}

export interface NodeOption {
  node: SkillNode;
  unlockable: boolean;
  reason?: BlockReason;
}

export interface OptionView {
  /** 全图平均启蒙度 */
  averageEnlightenment: number;
  /** 距离通关还差多少（0–1，1 = 达标） */
  goalProgress: number;
  turn: number;
  turnLimit: number;
  /** 能量上限（含技能加成） */
  energyCap: number;
  litCount: number;
  regionCount: number;
  effects: SkillEffects;
  routes: RouteOption[];
  regions: RegionOption[];
  nodes: NodeOption[];
}

export function averageEnlightenment(state: SimState): number {
  const regions = Object.values(state.regions);
  if (regions.length === 0) return 0;
  return regions.reduce((sum, r) => sum + r.enlightenment, 0) / regions.length;
}

export function countLit(state: SimState): number {
  return Object.values(state.regions).filter((r) => r.lit).length;
}

/**
 * 路线"结构上"是否可用（不看能量、不看目标是否已满）：
 * 已显现、未被封锁、所需知识卡已收集。
 */
export function routeBlockReasonIgnoringEnergy(
  setup: MatchSetup,
  state: SimState,
  route: Route,
): BlockReason | null {
  if (state.closedRouteIds.includes(route.id)) return "route_closed";
  if (route.hiddenByDefault && !state.revealedRouteIds.includes(route.id)) {
    return "route_not_revealed";
  }
  if (route.requiredCardId && !state.unlockedCardIds.includes(route.requiredCardId)) {
    return "route_need_card";
  }
  return null;
}

/** 路线当前不可用的原因；null 表示可用 */
export function routeBlockReason(
  setup: MatchSetup,
  state: SimState,
  routeId: string,
  effects: SkillEffects = collectEffects(setup.skillTree, state.unlockedNodeIds),
): BlockReason | null {
  const route = setup.map.routes.find((r) => r.id === routeId);
  if (!route) return "route_unknown";

  const structural = routeBlockReasonIgnoringEnergy(setup, state, route);
  if (structural) return structural;

  if (!state.regions[route.from]?.ignited) return "source_not_ignited";
  const target = state.regions[route.to];
  if (!target) return "route_unknown";
  if (target.enlightenment >= 100) return "region_full";
  if (state.energy < spreadCost(route.cost, effects)) return "insufficient_energy";
  return null;
}

/** 该区域能否开辟新营地；null 表示可以 */
export function igniteBlockReason(
  setup: MatchSetup,
  state: SimState,
  regionId: string,
): BlockReason | null {
  const region = state.regions[regionId];
  if (!region) return "region_unknown";
  if (region.ignited) return "already_ignited";
  if (!hasIgnitedNeighbour(setup, state, regionId)) return "not_adjacent";
  if (state.energy < IGNITE_COST) return "insufficient_energy";
  return null;
}

/** 该节点能否升级；null 表示可以 */
export function nodeUpgradeReason(
  setup: MatchSetup,
  state: SimState,
  nodeId: string,
): BlockReason | null {
  const node = setup.skillTree.find((n) => n.id === nodeId);
  if (!node) return "node_unknown";
  if (state.unlockedNodeIds.includes(node.id)) return "node_already_unlocked";
  if (node.parentId && !state.unlockedNodeIds.includes(node.parentId)) return "node_need_parent";
  if (state.wisdom < node.cost) return "insufficient_wisdom";
  return null;
}

/** 与"已点燃区域"相邻的区域 id（可开辟新营地的候选） */
export function reachableRegionIds(setup: MatchSetup, state: SimState): string[] {
  const reachable = new Set<string>();
  for (const route of setup.map.routes) {
    if (routeBlockReasonIgnoringEnergy(setup, state, route)) continue;
    if (state.regions[route.from]?.ignited) reachable.add(route.to);
    if (state.regions[route.to]?.ignited) reachable.add(route.from);
  }
  return [...reachable];
}

function hasIgnitedNeighbour(setup: MatchSetup, state: SimState, regionId: string): boolean {
  for (const route of setup.map.routes) {
    if (routeBlockReasonIgnoringEnergy(setup, state, route)) continue;
    if (route.from === regionId && state.regions[route.to]?.ignited) return true;
    if (route.to === regionId && state.regions[route.from]?.ignited) return true;
  }
  return false;
}

/** 一次算清 UI 需要的全部局势信息 */
export function describeOptions(setup: MatchSetup, state: SimState): OptionView {
  const constants = resolveConstants(setup.constants);
  const effects = collectEffects(setup.skillTree, state.unlockedNodeIds);
  const avg = averageEnlightenment(state);

  return {
    averageEnlightenment: avg,
    goalProgress: setup.goalEnlightenment > 0 ? Math.min(1, avg / setup.goalEnlightenment) : 0,
    turn: state.turn,
    turnLimit: constants.turnLimit,
    energyCap: constants.energyCap + effects.maxEnergyBonus,
    litCount: countLit(state),
    regionCount: Object.keys(state.regions).length,
    effects,
    routes: setup.map.routes.map((route) => {
      const reason = routeBlockReason(setup, state, route.id, effects);
      return {
        route,
        usable: reason === null,
        cost: spreadCost(route.cost, effects),
        targetEnlightenment: state.regions[route.to]?.enlightenment ?? 0,
        ...(reason ? { reason } : {}),
      };
    }),
    regions: setup.map.regions.map((region) => {
      const reason = igniteBlockReason(setup, state, region.id);
      return { region, ignitable: reason === null, ...(reason ? { reason } : {}) };
    }),
    nodes: setup.skillTree.map((node) => {
      const reason = nodeUpgradeReason(setup, state, node.id);
      return { node, unlockable: reason === null, ...(reason ? { reason } : {}) };
    }),
  };
}
